/**
 * Cache wrapper for the weather range endpoint. Wraps `API.Weather.range(...)` with a
 * `TtlCache` keyed by (quantized lat/lon, ISO date range). Survives page nav (e.g.
 * Robots → JobDiagnostics → back) so revisiting the same shift doesn't re-hit the
 * backend.
 *
 * TTL: 30 minutes. Weather data for archived dates (>5 days old) is immutable, so the
 * TTL is mostly for the rare case of viewing a recent shift whose archive backfilled
 * since the last fetch — we want fresh data eventually, but not on every page nav.
 */

import { TtlCache } from "../cache/ttlCache";
import { API } from "../api";
import type { WeatherRange } from "../api/routers/weather";

const RANGE_TTL_MS = 30 * 60 * 1000;

/** Key format: `${lat_q},${lon_q},${startMs},${endMs}`. Lat/lon are quantized to 0.01°
 *  to match the backend's S3 cache key, so two callers with slightly-different raw coords
 *  still collide on the same entry. The fetcher parses the key back into args. */
const buildKey = (lat: number, lon: number, start: Date, end: Date): string => {
	const lq = Math.round(lat * 100) / 100;
	const lnq = Math.round(lon * 100) / 100;
	return `${lq},${lnq},${start.getTime()},${end.getTime()}`;
};

class CachedWeatherRange {
	private cache = new TtlCache<string, WeatherRange>(RANGE_TTL_MS, async (key) => {
		const [lat, lon, startMs, endMs] = key.split(",").map(Number);
		return API.Weather.range(lat, lon, new Date(startMs), new Date(endMs));
	});

	public peek(lat: number, lon: number, start: Date, end: Date): WeatherRange | null {
		return this.cache.peek(buildKey(lat, lon, start, end));
	}

	public get(lat: number, lon: number, start: Date, end: Date): Promise<WeatherRange> {
		return this.cache.get(buildKey(lat, lon, start, end));
	}

	public clear(): void {
		this.cache.clear();
	}
}

export class CachedWeatherAPI {
	public range = new CachedWeatherRange();
}
